import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { twMerge } from 'tailwind-merge';

interface ItemPaginatorProps {
  /** Total number of items to paginate through */
  totalItems: number;
  /** Currently active item index (0-based) */
  activeIndex: number;
  /** Called when the user selects a specific item by index */
  onNavigate: (index: number) => void;
  className?: string;
}

const baseTranslationKey = 'components.elements.ItemPaginator' as const;

const ItemPaginator: React.FC<ItemPaginatorProps> = ({ totalItems, activeIndex, onNavigate, className }) => {
  const { isMobileView } = useMobileView();

  if (totalItems <= 0) return null;

  const clampedIndex = Math.min(Math.max(activeIndex, 0), totalItems - 1);

  return (
    <nav
      className={twMerge('gap-xs flex justify-center', className)}
      role="navigation"
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' && clampedIndex > 0) {
          e.preventDefault();
          onNavigate(clampedIndex - 1);
        } else if (e.key === 'ArrowRight' && clampedIndex < totalItems - 1) {
          e.preventDefault();
          onNavigate(clampedIndex + 1);
        }
      }}>
      {Array.from({ length: totalItems }).map((_, index) => (
        <button
          key={index}
          type="button"
          aria-label={_t(`${baseTranslationKey}.goToItem`, { current: index + 1, total: totalItems })}
          aria-current={clampedIndex === index ? 'true' : undefined}
          style={getDotSize(isMobileView, clampedIndex === index)}
          className={twMerge(
            'focus-visible:ring-fg_link rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2',
            clampedIndex === index ? 'bg-bg_secondary_selected' : 'bg-bg_secondary_pressed',
          )}
          onClick={() => onNavigate(index)}
        />
      ))}
    </nav>
  );
};

function getDotSize(isMobile: boolean, isActive: boolean): React.CSSProperties {
  if (isMobile) {
    return { width: isActive ? 17 : 8, height: 8 };
  }
  return { width: isActive ? 32 : 24, height: 8 };
}

export default ItemPaginator;
